/**
 * Record a payment against one bill instance — in full or in part.
 *
 * A bill instance has MANY payments (docs/01 §7.1), so a partial payment is a
 * first-class outcome rather than an edge case: the amount defaults to what is
 * outstanding, and typing a smaller number simply leaves the rest outstanding.
 *
 * Recording it also creates the shared expense it implies (docs/01 §7.2 — "one
 * ledger, not two"): if one owner pays the electricity in full, the others owe
 * their share immediately. The expense is for what was ACTUALLY PAID, not what
 * was due, so a part-payment creates a part-sized shared expense.
 */

import { type FormEvent, useState } from 'react'
import type {
  Bill,
  BillInstance,
  MemberId,
  PaymentMethod,
  TrustedClock,
} from '@office/domain'
import {
  type SplitParticipant,
  billInstanceView,
  billStatus,
  personIdOf,
} from '@office/domain/money'
import { piastres } from '@office/domain'
import type { MoneyActions } from './actions.js'
import { type MoneySnapshot, guestPredicate, nameOf } from './data.js'
import { amountInputValue, describeDue, formatDay, formatInstant, parseAmount } from './format.js'
import { AmountInput, Badge, Banner, Money, RadioGroup, StatusBadge } from './ui.js'
import './money.css'

const METHODS: readonly { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
]

export interface RecordBillPaymentFormProps {
  snapshot: MoneySnapshot
  clock: TrustedClock
  actorId: MemberId
  bill: Bill
  instance: BillInstance
  actions: MoneyActions
  onDone?: () => void
}

export function RecordBillPaymentForm({
  snapshot,
  clock,
  actorId,
  bill,
  instance,
  actions,
  onDone,
}: RecordBillPaymentFormProps) {
  const now = clock.now()
  const view = billInstanceView(instance, snapshot.payments, now)

  // Nothing outstanding means nothing to pay. Re-offering the full amount on a
  // settled instance turns one stray tap into a second full payment and a
  // second full shared expense, so the field starts empty and the button stays
  // disabled until someone types a figure on purpose.
  const [text, setText] = useState(() =>
    view.outstanding > 0 ? amountInputValue(view.outstanding) : '',
  )
  const [method, setMethod] = useState<PaymentMethod>('cash')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /** Empty `splitAmong` means every owner (docs/01 §7.2). */
  const shareholders =
    bill.splitAmong.length > 0
      ? bill.splitAmong.map(m => personIdOf(m))
      : snapshot.members.map(m => m.id)

  // A guest's share is marked settled-on-the-day at the moment it is WRITTEN
  // (docs/01 §7.2), exactly as the expense form and the wall sheet do it.
  // `bill.splitAmong` is an unconstrained `uuid[]` of people ids, so a guest
  // can end up named on a bill; storing their share unflagged would leave the
  // row relying on a runtime lookup to stay honest, and that lookup is over
  // the mirror's CURRENT, unarchived people. Archive the guest and the flag
  // would be the only thing left saying they never owed anything — so the flag
  // is what gets stored.
  //
  // And it is decided by `guestPredicate`, which reads `allPeople`, NOT by
  // `snapshot.guests`, which is the live roster with archived rows dropped.
  // Guests get archived once they stop visiting, and a bill written while one
  // was still around outlives the archiving: reading the live roster would flag
  // exactly the guests who are still visiting and quietly leave the flag OFF
  // for the ones the paragraph above is about.
  const isGuest = guestPredicate(snapshot)
  const participants: readonly SplitParticipant[] = shareholders.map(personId =>
    isGuest(personId) ? { personId, settledImmediately: true } : { personId },
  )

  // A payment with nobody to split it between cannot become a shared expense,
  // and a bill payment that is not also a shared expense is the two-ledger
  // problem docs/01 §7.2 exists to prevent. Refuse it here rather than letting
  // the write fail halfway.
  const nobodyToSplitWith = participants.length === 0

  const amount = parseAmount(text)
  const canSubmit = amount !== null && amount > 0 && !nobodyToSplitWith && !busy

  const afterPaid = piastres(view.paid + (amount ?? 0))
  const afterOutstanding = piastres(instance.amountDue - afterPaid)
  const afterStatus = billStatus(instance.amountDue, afterPaid)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit || amount === null) return
    setBusy(true)
    setError(null)
    try {
      await actions.payBill({ bill, instance, amount, method, participants })
      onDone?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record that payment')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="m-screen" onSubmit={submit} aria-labelledby="paybill-title">
      <h2 className="m-title" id="paybill-title">
        {bill.name} — {instance.periodLabel}
      </h2>
      <p className="m-asof">
        Due {formatDay(instance.dueDay)} · {describeDue(instance.dueDay, now)}
      </p>
      <p className="m-asof">Paid by {nameOf(snapshot, actorId)} · {formatInstant(now)}</p>

      <dl className="m-facts">
        <div>
          <dt>Expected</dt>
          <dd><Money value={instance.amountDue} /></dd>
        </div>
        <div>
          <dt>Paid so far</dt>
          <dd><Money value={view.paid} /></dd>
        </div>
        <div>
          <dt>Outstanding</dt>
          <dd><Money value={view.outstanding} /></dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd><StatusBadge status={view.status} overdue={view.overdue} /></dd>
        </div>
      </dl>

      <AmountInput
        id="paybill-amount"
        label="Amount to pay now (EGP)"
        value={text}
        onChange={setText}
        invalid={text.length > 0 && amount === null}
        hint={
          text.length > 0 && amount === null
            ? 'Enter an amount like 250 or 250.50'
            : view.outstanding > 0
              ? 'Pay less than the outstanding amount to record a partial payment.'
              : 'This period is already settled — enter an amount only to record an extra payment.'
        }
      />

      <RadioGroup
        legend="Method"
        name="paybill-method"
        options={METHODS}
        value={method}
        onChange={setMethod}
      />

      {nobodyToSplitWith && (
        <Banner tone="warn">
          Nobody to split this between yet, so it cannot become a shared expense.
          Wait for the owners to sync, or name them on the bill.
        </Banner>
      )}

      <div className="m-preview" aria-live="polite" data-testid="paybill-preview">
        {amount === null ? (
          <p className="m-empty-note">Enter an amount to see what it leaves.</p>
        ) : (
          <>
            <p>
              After this: <Money value={afterPaid} /> paid,{' '}
              <Money value={piastres(Math.max(0, afterOutstanding))} /> outstanding{' '}
              <Badge tone={afterStatus === 'paid' ? 'ok' : afterStatus === 'partial' ? 'primary' : 'neutral'}>
                {afterStatus}
              </Badge>
            </p>
            <p className="m-note" data-testid="paybill-share-note">
              {nobodyToSplitWith ? (
                'This cannot be recorded until there is somebody to split it between.'
              ) : (
                <>
                  This also adds a shared expense of <Money value={amount} />, split equally
                  between {shareholders.map(p => nameOf(snapshot, p)).join(', ')}.
                </>
              )}
            </p>
          </>
        )}
      </div>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <div className="m-actions">
        <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
          Record payment
        </button>
      </div>
    </form>
  )
}
